import { Col, Row } from 'antd';
import { InfoOutlined } from '@ant-design/icons';
import { useEvent, useStore, useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContainerInner from '../../components/ContentContainer/ContainerInner.jsx';
import MapWork from '../MapWork/MapWork.jsx';
import DashboardCharts from '../../components/DashboardCharts/DashboardCharts.jsx';
import {
  $activeFilters,
  changeActiveFilterEv,
  resetActiveFiltersEv,
} from '../../models/activeFiltersModel/index.js';
import OverviewModal from './components/OverviewModal.jsx';
import {
  $clusterNiche,
  $clusterSelect,
  $isModalOpen,
  $otpStore,
  $overviewStore,
  changeClusterNicheEv,
  changeClusterSelectEv,
  getOverviewDataFx,
  ManufacturersGate,
  setIsModalOpenEv,
  setOtpModalEv,
  toggleClusterInfoModalEv,
} from '../../models/overviewModel/index.js';
import { months_dictionary } from '../../dictionaries/months_dictionary.js';
import { formatNumber } from '../../utils/number-manipulation.js';
import { hasActiveFilters } from '../../utils/active-filters-utils.js';
import OverviewDashboardModal from './components/OverviewDashboardModal.jsx';
import ManufacturersModal from './components/otp/ManufacturersModal.jsx';
import OtpCharts from '../../components/OtpCharts/OtpCharts.jsx';
import OverviewCard from './components/OverviewCard.jsx';
import { initData as data } from './utils.js';
import GovSwitch from './components/GovSwitch.jsx';
import OverviewBizCard from './components/biz/OverviewBizCard.jsx';
import BizRegionModal from './components/biz/BizRegionModal.jsx';
import ClusterInfoCards from '../../components/ClusterInfoCards/ClusterInfoCards.jsx';
import ClusterInfoModal from '../../components/ClusterInfoModal/ClusterInfoModal.jsx';
import { $dictionariesStore } from '../../models/dictionariesModel/index.js';
import ClusterProblemDetails from '../../components/ClusterInfoModal/ClusterProblemDetails.jsx';
import InvestProjectsModals from '../../components/InvestProjectsComponents/InvestProjectsModals.jsx';
import { $userInfo } from '../../models/authModel/index.js';
import MapModal from '../../components/MapModal/MapModal.jsx';
import InvProjectTabs from './components/InvProjectTabs.jsx';
import OverviewClusterCard from './components/OverviewClusterCard.jsx';
import { InfoCircle } from '../../utils/customIcons.jsx';

function Overview() {
  const curUser = useUnit($userInfo);
  const changeActiveFilters = useUnit(changeActiveFilterEv);
  const activeFilters = useUnit($activeFilters);
  const { visible, modalType } = useStore($isModalOpen);
  const changeIsModalOpen = useEvent(setIsModalOpenEv);
  const [cardsData, setCardsData] = useState(data);
  const overviewStore = useStore($overviewStore);
  const {
    medium_companies,
    people,
    taxes,
    mio_performance,
    business_inspections,
    problems,
    projects,
  } = overviewStore;
  const { companies, avg_workload } = useStore($otpStore);
  const setOtpModal = useEvent(setOtpModalEv);

  const resetActiveFilters = useEvent(resetActiveFiltersEv);
  const toggleClusterInfoModal = useEvent(toggleClusterInfoModalEv);
  const clusterSelect = useStore($clusterSelect);
  const changeClusterSelect = useEvent(changeClusterSelectEv);
  const clusterNiche = useStore($clusterNiche);
  const changeClusterNiche = useEvent(changeClusterNicheEv);

  const onChangeClusterNiche = useCallback(
    (v) => {
      changeClusterNiche(v);
      if (v === 'ГМК') {
        changeActiveFilters({
          field: 'project_cluster_id',
          value: 3,
        });
      } else {
        changeActiveFilters({
          field: 'project_cluster_id',
          value: 1,
        });
      }
    },
    [changeClusterNiche, changeActiveFilters]
  );

  const onChangeCluster = useCallback(
    (v) => {
      changeClusterSelect(v);
      changeActiveFilters({
        field: 'project_cluster_id',
        value: v,
      });
    },
    [changeClusterSelect, changeActiveFilters]
  );

  const { stages } = useUnit($dictionariesStore);
  const { t } = useTranslation();

  useEffect(() => {
    if (hasActiveFilters(activeFilters)) {
      setTimeout(() => {
        resetActiveFilters();
      }, 500);
    }
  }, []);

  const cardClick = (e) => {
    let node = e.target;
    if (node.dataset.subcategory == activeFilters.subCategory) {
      return;
    }

    if (node.localName === 'svg' || node.localName === 'button') return;

    if (!node?.dataset) return;

    while (Object.keys(node?.dataset ?? {})?.length === 0) {
      node = node.parentNode;
    }

    if (node.dataset.subcategory == activeFilters.subCategory) {
      return;
    }
    if (node.dataset.type === 'card') {
      if (activeFilters.subCategory !== node.dataset.subcategory) {
        changeActiveFilters({
          field: 'subCategory',
          value: node.dataset.subcategory,
        });
      }
    } else {
      changeActiveFilters({
        field: 'subCategory',
        value: node.dataset.subcategory,
      });
    }
  };

  const prepareCardsData = () => {
    const reformatCard = (newData, card, plainValue, quarter) => {
      const formattedNumber = formatNumber(
        plainValue ? newData : newData.value
      );
      const [val, measure] = formattedNumber.split(' ');
      let type;
      if (plainValue) {
        type =
          newData === 0 ? 'neutral' : newData > 0 ? 'positive' : 'negative';
      } else {
        type =
          newData?.change === 0
            ? 'neutral'
            : newData?.change > 0
            ? 'positive'
            : 'negative';
      }

      return {
        ...card,
        value: val,
        measure: `${measure || ''} ${card?.measure}`,
        date: `${
          quarter
            ? `${newData.month} кв`
            : months_dictionary[newData?.month] ?? 'Нет данных'
        } ${newData.year ? newData.year : new Date().getFullYear()}`,
        granularity: !!quarter,
        percent: newData?.change?.toFixed(2)?.replace('-', ''),
        type,
      };
    };
    const mutatedData = {
      ...data,
      mal: {
        ...data.mal,
        cards: [
          {
            ...reformatCard(people.last_month_data, data.mal.cards[0]),
          },
          {
            ...reformatCard(
              taxes.last_month_data,
              data.mal.cards[1],
              null,
              true
            ),
          },
        ],
      },
      sred: {
        ...data.sred,
        cards: [
          {
            ...reformatCard(
              medium_companies.last_month_data,
              data.sred.cards[0],
              null,
              true
            ),
          },
        ],
      },
      projectsRealisation: {
        ...data.projectsRealisation,
        cards: [
          {
            ...data.projectsRealisation.cards[0],
            value: projects.count,
            measure: `(${formatNumber(projects.budget * 1000000)} тенге)`,
            popovers: [
              {
                title:
                  'Сколько будет рабочих мест и сколько налогов будет оплачено',
                items: [
                  {
                    label: 'Рабочих мест',
                    value: new Intl.NumberFormat('ru-KZ', {
                      notation: 'standard',
                    }).format(projects.potential_people),
                  },
                  {
                    label: 'Налоги',
                    value: new Intl.NumberFormat('ru-KZ', {
                      notation: 'standard',
                    }).format(projects?.potential_taxes?.toFixed(2) ?? 0),
                  },
                ],
                icon: InfoCircle,
              },
            ],
          },
          {
            ...data.projectsRealisation.cards[1],
            value: problems.count,
            measure: `(${formatNumber(problems.budget * 1000000)} тенге)`,
            popovers: [
              {
                title: 'Список проблем по видам:',
                items: problems.stage_problems
                  ? Object.keys(problems.stage_problems).map((item) => {
                      return {
                        label: stages[item]?.name,
                        value: problems?.stage_problems[item],
                      };
                    })
                  : [],
                icon: InfoCircle,
              },
            ],
          },
        ],
      },
      businessClimate: {
        ...data.businessClimate,
        cards: [
          {
            ...reformatCard(
              business_inspections.last_month_data,
              data.businessClimate.cards[0],
              false,
              true
            ),
            header: t('dashboard.business_climate.Малый и микро бизнес'),
            // title: t(
            //   'dashboard.business_climate.Оценка эффективности деятельности МИО'
            // ),
          },
          {
            ...reformatCard(
              mio_performance.last_month_data,
              data.businessClimate.cards[1],
              false,
              true
            ),
            header: t('dashboard.business_climate.Средний бизнес'),
            // title: t(
            //   'dashboard.business_climate.Оценка эффективности деятельности МИО'
            // ),
          },
        ],
      },
      otp: {
        ...data.otp,
        cards: [
          {
            ...reformatCard(companies ?? 0, data.otp.cards[0], true),
          },
          {
            ...reformatCard(
              avg_workload ? avg_workload * 100 : 0,
              data.otp.cards[1],
              true
            ),
          },
        ],
      },
      cluster: {
        ...data.cluster,
        cards: [
          {
            ...data.cluster.cards[0],
            infoModal: !!clusterNiche ? toggleClusterInfoModal : null,
            noButton: true,
          },
          {
            ...data.cluster.cards[1],
            infoModal: !!clusterNiche ? toggleClusterInfoModal : null,
          },
        ],
      },
    };

    setCardsData(mutatedData);
  };

  useEffect(() => {
    if (
      Object.hasOwn(curUser, 'user_agreement_confirmed') &&
      curUser?.user_agreement_confirmed === true
    )
      getOverviewDataFx({
        index: 'people',
      });
  }, [curUser]);

  useEffect(() => {
    prepareCardsData();
  }, [overviewStore, companies]);

  return (
    <ContainerInner
      extra={<GovSwitch changeActiveFilters={changeActiveFilters} />}
    >
      <ManufacturersGate />
      <MapModal />
      <Row gutter={[8, 8]} align={'stretch'}>
        <Col span={14}>
          <Row gutter={[8, 8]}>
            <Col span={13}>
              <OverviewCard
                {...cardsData.mal}
                cardClick={cardClick}
                field="mal"
                activeCategory={activeFilters.category}
                activeSubCategory={activeFilters.subCategory}
              />
            </Col>
            <Col span={11}>
              <OverviewCard
                {...cardsData.sred}
                cardClick={cardClick}
                field="sred"
                activeCategory={activeFilters.category}
                activeSubCategory={activeFilters.subCategory}
              />
            </Col>
            <Col span={13}>
              <OverviewCard
                {...cardsData.projectsRealisation}
                cardClick={cardClick}
                field="projectsRealisation"
                activeCategory={activeFilters.category}
                activeSubCategory={activeFilters.subCategory}
                // предположил что с нижней компоненты InvProjectTabs
                setIsOpen={changeIsModalOpen}
              />
              {/*<InvProjectTabs*/}
              {/*  invData={cardsData.projectsRealisation}*/}
              {/*  otpData={cardsData.otp}*/}
              {/*  cardClick={cardClick}*/}
              {/*  activeFilters={activeFilters}*/}
              {/*  changeIsModalOpen={changeIsModalOpen}*/}
              {/*  setOtpModal={setOtpModal}*/}
              {/*/>*/}
            </Col>
            <Col span={11}>
              <OverviewBizCard
                {...cardsData.businessClimate}
                title={t('dashboard.business_climate.title')}
                cardClick={cardClick}
                field="businessClimate"
                activeCategory={activeFilters.category}
                activeSubCategory={activeFilters.subCategory}
                granularity={activeFilters.business_climate_yearly}
                changeFilters={changeActiveFilters}
              />
            </Col>
            <Col span={24}>
              <OverviewClusterCard
                {...cardsData.cluster}
                cardClick={cardClick}
                field="cluster"
                activeCategory={activeFilters.category}
                activeSubCategory={activeFilters.subCategory}
                showInfo={false}
                niche={clusterNiche}
                nicheCluster={clusterSelect}
                selects={[
                  {
                    value: clusterNiche,
                    type: 'niche',
                    onChange: onChangeClusterNiche,
                    options: [
                      {
                        label: 'ГМК',
                        value: 'ГМК',
                      },
                      {
                        label: 'АПК',
                        value: 'АПК',
                      },
                    ],
                  },
                  {
                    value: clusterSelect,
                    type: 'nicheCluster',
                    onChange: onChangeCluster,
                    options:
                      clusterNiche === 'АПК'
                        ? [
                            {
                              label: 'Птицеводство',
                              value: 1,
                            },
                            {
                              label: 'Овцеводство',
                              value: 2,
                            },
                          ]
                        : [
                            {
                              label: 'Медь',
                              value: 3,
                            },
                          ],
                  },
                ]}
              />
            </Col>
          </Row>
        </Col>
        <Col span={10}>
          <Row gutter={[0, 8]} align={'stretch'}>
            <Col
              span={24}
              className={'map_container'}
              style={{
                height: '350px',
                borderRadius: '12px',
                overflow: 'hidden',
                // padding: '0',
                // border: '1px solid black',
              }}
            >
              <MapWork isOverview />
            </Col>
            <Col span={24}>
              {activeFilters.subCategory !== 'otp_count' &&
              activeFilters.subCategory !== 'otp_avg_workload' &&
              activeFilters.subCategory !== 'target_indicators' &&
              activeFilters.subCategory !== 'top_problems' ? (
                <DashboardCharts />
              ) : (
                ''
              )}
              {activeFilters.subCategory === 'otp_count' ||
              activeFilters.subCategory === 'otp_avg_workload' ? (
                <OtpCharts />
              ) : null}
              {/* {activeFilters.subCategory === 'target_indicators' || */}
              {/* activeFilters.subCategory === 'top_problems' ? ( */}
              {/*  <ClusterInfoCards /> */}
              {/* ) : null} */}
            </Col>
          </Row>
        </Col>
      </Row>
      <OverviewDashboardModal />
      <OverviewModal
        isOpen={visible}
        setIsOpen={changeIsModalOpen}
        type={modalType}
      />
      <ManufacturersModal />
      <BizRegionModal />
      <ClusterInfoModal />
      <ClusterProblemDetails />
      <InvestProjectsModals />
    </ContainerInner>
  );
}

export default Overview;
